import { MINUTE } from "../../../../engine/state";
import type { TournamentState } from "../../../../engine/types";

/**
 * ?demo=lunch|lunchover|held|soon|waiting on the wall, a tablet or a phone: a part of the day that
 * the pretend day never reaches by itself, on this screen only.
 * lunch is a break with thirty minutes to go (&label=Food for other words, &text=... for a
 * message sent during it); lunchover is the same break with its time passed. held is between
 * rounds with not one score in for the last round; soon is the two minutes after a late score
 * let that go, counting down to the next round (both with rounds starting by themselves); waiting
 * is between rounds waiting for an admin's Start, the usual since decision 41.
 */
const KINDS = ["lunch", "lunchover", "held", "soon", "waiting"] as const;
export type DemoDay = (typeof KINDS)[number] | null;

export function demoDayFromLocation(): DemoDay {
  const d = new URLSearchParams(location.search).get("demo");
  return KINDS.find((x) => x === d) ?? null;
}

/** The day as `kind` asks for it. `openedAt` is when the page opened, in the server's time. */
export function demoDay(s: TournamentState, kind: DemoDay, openedAt: number): TournamentState {
  if (!kind) return s;
  const q = new URLSearchParams(location.search);
  if (kind === "lunch" || kind === "lunchover") {
    const text = q.get("text");
    return {
      ...s,
      pausedAll: true,
      pausedUntil: openedAt + (kind === "lunch" ? 30 * MINUTE : -MINUTE),
      pauseLabel: q.get("label") || "Lunch",
      clock: { ...s.clock, pausedAt: s.clock.pausedAt ?? openedAt },
      notice: text ? { text, at: openedAt, by: "demo" } : s.notice,
    };
  }
  const idle = { phase: "idle", roundNo: null, phaseEndsAt: null, pausedAt: null } as const;
  if (kind === "waiting") return { ...s, clock: idle, config: { ...s.config, autoAdvance: false } };
  const last = s.rounds.length - 1;
  if (kind === "soon") {
    return {
      ...s,
      clock: idle,
      config: { ...s.config, autoAdvance: true, changeoverMs: 2 * MINUTE, lastWhistleAt: null },
      rounds: s.rounds.map((r, i) => {
        if (i !== last) return r;
        const late = { a: 11, b: 7, capped: true };
        const scored = r.games.some((g) => g.score || g.voided);
        return { ...r, releasedAt: openedAt, games: scored ? r.games : r.games.map((g, j) => (j === 0 ? { ...g, score: late } : g)) };
      }),
    };
  }
  return {
    ...s,
    clock: idle,
    config: { ...s.config, autoAdvance: true },
    rounds: s.rounds.map((r, i) => (i === last ? { ...r, games: r.games.map((g) => ({ ...g, score: null, voided: false })) } : r)),
  };
}
